import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Tag, Heart, LogOut, User, MapPin, Plus, ImageOff, MailWarning } from "lucide-react";
import { toast } from "sonner";
import userApi from "@/lib/userApi";
import { useAppAuth } from "@/context/AppAuthContext";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import { timeAgo } from "@/lib/format";

const STATUS_LABEL = { pending: "Изчаква одобрение", published: "Публикувана", expired: "Изтекла", rejected: "Отхвърлена" };
const STATUS_BADGE = {
  pending: "bg-amber-50 text-amber-700",
  published: "bg-green-50 text-green-700",
  expired: "bg-slate-200 text-slate-600",
  rejected: "bg-slate-100 text-slate-500",
};

function backendResolve(src) {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  return src && src.startsWith("/") ? `${backend}${src}` : src;
}

function ListingRow({ l, showStatus }) {
  return (
    <Link
      to={l.status === "published" ? `/obyava/${l.slug}` : "#"}
      className={`flex gap-3 rounded-xl border border-slate-200 bg-white p-3 ${l.status === "published" ? "hover:border-primary/40" : "cursor-default"}`}
      data-testid={`profile-listing-${l.id}`}
    >
      <div className="h-16 w-24 shrink-0 overflow-hidden rounded-lg bg-slate-100">
        {(l.images || [])[0] ? (
          <img src={backendResolve(l.images[0])} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-slate-300"><ImageOff className="h-5 w-5" /></div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-bold text-primary"><Tag className="h-3 w-3" />{l.listing_type}</span>
          {showStatus && <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_BADGE[l.status]}`}>{STATUS_LABEL[l.status]}</span>}
          {l.price && <span className="text-sm font-bold text-primary">{l.price}</span>}
        </div>
        <h3 className="mt-1 truncate font-display text-sm font-bold text-slate-900">{l.title}</h3>
        <div className="mt-0.5 flex items-center gap-3 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{l.location}</span>
          {l.published_at && <span>{timeAgo(l.published_at)}</span>}
        </div>
      </div>
    </Link>
  );
}

export default function Profile() {
  const { user, loading, logout } = useAppAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("listings");
  const [resending, setResending] = useState(false);

  const resendVerification = async () => {
    setResending(true);
    try {
      const { data } = await userApi.post("/account/resend-verification");
      toast.success(data.message || "Изпратихме нова връзка.");
    } catch (e) {
      toast.error("Неуспешно изпращане. Опитайте пак.");
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    if (!loading && !user) navigate("/vhod", { state: { from: "/profil" } });
  }, [loading, user, navigate]);

  const { data: myListings, isLoading: l1 } = useQuery({
    queryKey: ["my-listings"],
    queryFn: () => userApi.get("/account/listings").then((r) => r.data),
    enabled: !!user,
  });
  const { data: favorites, isLoading: l2 } = useQuery({
    queryKey: ["my-favorites"],
    queryFn: () => userApi.get("/account/favorites").then((r) => r.data),
    enabled: !!user,
  });

  if (loading || !user) {
    return (
      <Layout showTicker={false}>
        <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      </Layout>
    );
  }

  return (
    <Layout showTicker={false}>
      <Seo title="Моят профил" noindex />
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8" data-testid="profile-page">
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-slate-900 p-6 text-white">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-xl font-bold">
              {(user.name || user.email || "?").charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="font-display text-xl font-extrabold" data-testid="profile-name">{user.name}</h1>
              <p className="text-sm text-slate-300">{user.email}</p>
              {user.phone && <p className="text-sm text-slate-400">{user.phone}</p>}
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/publikuvai-obyava" className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-bold text-white hover:bg-[#0041A8]">
              <Plus className="h-4 w-4" />Нова обява
            </Link>
            <button onClick={() => { logout(); navigate("/"); }} data-testid="profile-logout" className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10">
              <LogOut className="h-4 w-4" />Изход
            </button>
          </div>
        </div>

        {!user.email_verified && (
          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="profile-verify-banner">
            <MailWarning className="h-5 w-5 shrink-0 text-amber-600" />
            <p className="flex-1 text-sm text-amber-800">
              Потвърдете имейла си, за да можете да публикувате обяви. Проверете пощата си или изпратете нова връзка.
            </p>
            <button
              onClick={resendVerification}
              disabled={resending}
              data-testid="profile-resend-verification"
              className="inline-flex items-center gap-1.5 rounded-full bg-amber-600 px-4 py-2 text-sm font-bold text-white hover:bg-amber-700 disabled:opacity-60"
            >
              {resending && <Loader2 className="h-4 w-4 animate-spin" />}Изпрати отново
            </button>
          </div>
        )}

        <div className="mt-6 flex gap-2">
          <button onClick={() => setTab("listings")} data-testid="profile-tab-listings" className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold ${tab === "listings" ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            <Tag className="h-4 w-4" />Моите обяви {myListings ? `(${myListings.length})` : ""}
          </button>
          <button onClick={() => setTab("favorites")} data-testid="profile-tab-favorites" className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold ${tab === "favorites" ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            <Heart className="h-4 w-4" />Любими {favorites ? `(${favorites.length})` : ""}
          </button>
        </div>

        <div className="mt-5 space-y-3">
          {tab === "listings" && (
            l1 ? <Loader2 className="mx-auto my-10 h-6 w-6 animate-spin text-primary" /> :
            (myListings || []).length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center text-slate-500" data-testid="profile-listings-empty">
                Още нямате обяви. <Link to="/publikuvai-obyava" className="font-semibold text-primary hover:underline">Публикувайте първата си обява</Link>.
              </div>
            ) : myListings.map((l) => <ListingRow key={l.id} l={l} showStatus />)
          )}
          {tab === "favorites" && (
            l2 ? <Loader2 className="mx-auto my-10 h-6 w-6 animate-spin text-primary" /> :
            (favorites || []).length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center text-slate-500" data-testid="profile-favorites-empty">
                Нямате запазени обяви. Отваряйте обява и натиснете <Heart className="inline h-4 w-4 text-red-500" />, за да я запазите.
              </div>
            ) : favorites.map((l) => <ListingRow key={l.id} l={l} />)
          )}
        </div>
      </div>
    </Layout>
  );
}
